import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api';
import { getLanguageDisplayName, copyToClipboard } from '../components/product-details/utils';
import {
  TranslationFormData,
  TranslationFormValidation,
  TranslationFlowStatus,
} from '../components/translation/types';

const DEFAULT_TARGET_LANGUAGES = [
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'es',
  'et',
  'fi',
  'fr',
  'ga',
  'hr',
  'hu',
  'it',
  'lt',
  'lv',
  'mt',
  'nl',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
] as const;

interface UseNewTranslationReturn {
  formData: TranslationFormData;
  validation: TranslationFormValidation;
  loading: boolean;
  saving: boolean;
  error: string | null;
  availableTargets: string[];
  isExistingProduct: boolean;
  sourceLanguageDisplay: string;
  targetLanguageDisplay: string;
  flowStatus: TranslationFlowStatus;
  updateField: <K extends keyof TranslationFormData>(field: K, value: TranslationFormData[K]) => void;
  generateTranslation: () => Promise<void>;
  saveTranslation: () => Promise<void>;
  copyTranslation: () => void;
  cancel: () => void;
}

export const useNewTranslation = (): UseNewTranslationReturn => {
  const api = useApi();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id: productIdFromRoute } = useParams();

  const productIdParam = searchParams.get('productId') || productIdFromRoute || null;

  const [formData, setFormData] = useState<TranslationFormData>({
    productId: productIdParam,
    name: '',
    sku: '',
    ean: '',
    sourceLanguage: 'en',
    targetLanguage: '',
    sourceText: '',
    translatedText: '',
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableTargets, setAvailableTargets] = useState<string[]>([]);
  const [flowStatus, setFlowStatus] = useState<TranslationFlowStatus>('idle');
  const prevTargetLanguageRef = useRef<string | null>(null);

  const isExistingProduct = !!productIdParam;
  const sourceLanguageDisplay = getLanguageDisplayName(formData.sourceLanguage);
  const targetLanguageDisplay = getLanguageDisplayName(formData.targetLanguage);

  const validation: TranslationFormValidation = {
    hasValidProduct: isExistingProduct || formData.name.trim().length > 0,
    hasValidSource: formData.sourceText.trim().length > 0,
    hasValidLanguage: formData.targetLanguage.length > 0,
    canTranslate: formData.sourceText.trim().length > 0 && formData.targetLanguage.length > 0,
    canSave:
      (isExistingProduct || formData.name.trim().length > 0) &&
      formData.sourceText.trim().length > 0 &&
      formData.targetLanguage.length > 0 &&
      formData.translatedText.trim().length > 0,
  };

  const buildProductPayload = useCallback(
    () =>
      formData.productId
        ? { id: formData.productId }
        : { name: formData.name, sku: formData.sku, ean: formData.ean },
    [formData.ean, formData.name, formData.productId, formData.sku]
  );

  const buildGeneratePayload = useCallback(
    (sourceText: string) => ({
      product: buildProductPayload(),
      source_language: formData.sourceLanguage,
      target_language: formData.targetLanguage,
      source_text: sourceText,
    }),
    [buildProductPayload, formData.sourceLanguage, formData.targetLanguage]
  );

  const buildSavePayload = useCallback(
    (sourceText: string, translatedText: string) => ({
      product: buildProductPayload(),
      source_language: formData.sourceLanguage,
      target_language: formData.targetLanguage,
      source_text: sourceText,
      translated_text: translatedText,
    }),
    [buildProductPayload, formData.sourceLanguage, formData.targetLanguage]
  );

  useEffect(() => {
    if (!productIdParam) return;

    const loadProduct = async () => {
      try {
        const product = await api.get(`/products/${productIdParam}`);
        setFormData((prev) => ({
          ...prev,
          productId: product.id,
          name: product.name,
          sku: product.sku || '',
          ean: product.ean || '',
          sourceLanguage: product.base_language || 'en',
        }));

        try {
          const baseTranslation = await api.get(`/products/${productIdParam}/translations/${product.base_language || 'en'}`);
          if (baseTranslation.text) {
            setFormData((prev) => ({
              ...prev,
              sourceText: baseTranslation.text,
            }));
          }
        } catch {
          // No base translation yet.
        }
      } catch {
        setError(t('errors.fetchFailed'));
      }
    };

    loadProduct();
  }, [productIdParam, api, t]);

  useEffect(() => {
    const loadTargets = async () => {
      try {
        const apiTargets = await api.get(`/glossaries/targets?source=${encodeURIComponent(formData.sourceLanguage)}`);
        const targetsSet = new Set<string>(
          (Array.isArray(apiTargets) ? apiTargets : [])
            .map((code) => String(code).toLowerCase())
            .filter(Boolean)
        );

        for (const code of DEFAULT_TARGET_LANGUAGES) {
          targetsSet.add(code);
        }
        targetsSet.delete(formData.sourceLanguage.toLowerCase());

        const targets = Array.from(targetsSet).sort();
        setAvailableTargets(targets);

        if (targets.length > 0) {
          setFormData((prev) => (prev.targetLanguage ? prev : { ...prev, targetLanguage: targets[0] }));
        }
      } catch {
        const fallback = DEFAULT_TARGET_LANGUAGES.filter((code) => code !== formData.sourceLanguage.toLowerCase()).sort();
        setAvailableTargets(fallback);
        if (fallback.length > 0) {
          setFormData((prev) => (prev.targetLanguage ? prev : { ...prev, targetLanguage: fallback[0] }));
        }
      }
    };

    loadTargets();
  }, [formData.sourceLanguage, api]);

  useEffect(() => {
    const prev = prevTargetLanguageRef.current;
    prevTargetLanguageRef.current = formData.targetLanguage;

    if (!prev || prev === formData.targetLanguage) {
      return;
    }

    setFormData((prevState) => ({
      ...prevState,
      translatedText: '',
    }));
    setFlowStatus('idle');
  }, [formData.targetLanguage]);

  const updateField = useCallback(<K extends keyof TranslationFormData>(field: K, value: TranslationFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'translatedText' || field === 'sourceText') {
      setFlowStatus((prev) => (prev === 'generated' || prev === 'dirty' ? 'dirty' : prev));
      return;
    }
    setFlowStatus('idle');
  }, []);

  const saveTranslation = useCallback(async () => {
    if (!validation.canSave) return;

    setSaving(true);
    setFlowStatus('saving');
    setError(null);

    try {
      const payload = buildSavePayload(formData.sourceText, formData.translatedText);

      const response = await api.post('/translations/save', payload);

      if (response && response.productId) {
        setFlowStatus('saved');
        navigate(`/products/${response.productId}`);
      } else {
        setFlowStatus('error');
        setError(t('translation.saveFailed'));
      }
    } catch (err) {
      setFlowStatus('error');
      if ((err as any)?.status === 400 || (err as any)?.status === 422 || (err as any)?.code === 'VALIDATION_ERROR') {
        setError(t('errors.validationFixFields'));
      } else {
        setError(t('translation.saveFailed'));
      }
    } finally {
      setSaving(false);
    }
  }, [api, buildSavePayload, formData.sourceText, formData.translatedText, validation.canSave, navigate, t]);

  const generateTranslation = useCallback(async () => {
    if (!validation.canTranslate) return;

    setLoading(true);
    setFlowStatus('generating');
    setError(null);

    try {
      const response = await api.post('/translations/generate-and-save', buildGeneratePayload(formData.sourceText));
      const nextSourceText = response?.converted_text || formData.sourceText;
      const nextTranslatedText = response?.translation || '';

      setFormData((prev) => ({
        ...prev,
        sourceText: nextSourceText,
        translatedText: nextTranslatedText,
        productId:
          response && typeof response.productId === 'string' && response.productId.trim().length > 0
            ? prev.productId || response.productId
            : prev.productId,
      }));

      setFlowStatus('generated');
    } catch (err: any) {
      setFlowStatus('error');
      if (err && err.code === 'NO_CREDITS') {
        setError(t('errors.noCredits'));
      } else if (err?.status === 400 || err?.status === 422 || err?.code === 'VALIDATION_ERROR') {
        setError(t('errors.validationFixFields'));
      } else {
        setError(t('translation.generateAndSaveFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [
    api,
    buildGeneratePayload,
    formData.sourceText,
    formData.targetLanguage,
    formData.sourceLanguage,
    validation.canTranslate,
    t,
  ]);

  const copyTranslation = useCallback(() => {
    if (formData.translatedText) {
      copyToClipboard(formData.translatedText);
    }
  }, [formData.translatedText]);

  const cancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return {
    formData,
    validation,
    loading,
    saving,
    error,
    availableTargets,
    isExistingProduct,
    sourceLanguageDisplay,
    targetLanguageDisplay,
    flowStatus,
    updateField,
    generateTranslation,
    saveTranslation,
    copyTranslation,
    cancel,
  };
};

